import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useRaisedLayer } from "./layer";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const tooltipVariants = cva(
  "z-50 overflow-hidden rounded-md px-3 py-1.5 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      variant: {
        // Themed surface — white in light mode, dark in dark mode (default).
        default: "border bg-popover text-popover-foreground",
        // Solid dark-gray tooltip in both themes (#262626). Used in the IFC
        // viewer so custom tooltips match the native ones (WHA-3766).
        dark: "border border-neutral-700 bg-neutral-800 text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> &
    VariantProps<typeof tooltipVariants>
>(({ className, variant, sideOffset = 4, style, ...props }, forwardedRef) => {
  const layer = useRaisedLayer(className, style, forwardedRef);
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={layer.ref}
        sideOffset={sideOffset}
        className={cn(tooltipVariants({ variant }), className)}
        style={layer.style}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
